import { handleResponseError } from '@/Helpers.js';
import Model from '@/models/Model.js';
import TermTemplatePeriod from '@/models/term-template/TermTemplatePeriod.js';

export default class TermTemplate extends Model {
    static entity = 'term_templates';

    static modelName = 'TermTemplate';

    static fields() {
        return {
            id: this.number(null),
            term_schedule_template_id: this.number(null),
            external_id: this.attr(null),
            title: this.attr(null),
            description: this.attr(null).nullable(),
            status: this.string('DRAFT'),
            term_total_periods: this.number(null).nullable(),
            term_total_breaks: this.number(null).nullable(),
            term_total_weeks: this.number(null).nullable(),
            term_total_courses: this.number(null).nullable(),
            term_total_credits: this.number(null).nullable(),
            total_launched_terms: this.number(null).nullable(),

            ...super.fields(),

            periods: this.hasMany(TermTemplatePeriod, 'term_schedule_template_id', 'id'),
        };
    }

    // Defaults
    get modelName() {
        return this.modelName;
    }

    static searchable() {
        return ['title'];
    }

    // API - GET
    static async fetch(id) {
        const endpoint = `/api/term-templates/${id}`;
        try {
            const { response } = await this.api().get(endpoint);
            return response;
        } catch (e) {
            handleResponseError(e);
        }

        return false;
    }

    static async getTermByIdWithPeriods(id) {
        await this.fetch(id);
        return this.query().with('periods').whereId(id).first();
    }

    static async copyTermTemplate(id) {
        const endpoint = `/api/term-templates/${id}/duplicate`;
        try {
            const { response } = await this.api().post(endpoint);
            return response.data.data;
        } catch (e) {
            handleResponseError(e);
        }

        return false;
    }

    // API - PUT
    static async sync(id, data) {
        const endpoint = `/api/term-templates/${id}`;
        try {
            const { response } = await this.api().put(endpoint, data);

            if (response.status === 200) {
                await this.store().dispatch('AutoSaveEventBus/updateSubscription', {
                    subscription: 'UPDATE_COMPONENT_KEY',
                    data: {
                        action: 'termTemplateUpdated',
                    },
                });

                return response;
            }
        } catch (e) {
            handleResponseError(e);
        }

        return false;
    }

    get term_periods() {
        return this.periods.filter(period => period.type === 'period');
    }

    get term_courses() {
        return this.term_periods.reduce((acc, period) => acc.concat(period.courses), []);
    }

    get expected_instruction_weeks() {
        return this.term_periods.reduce((acc, period) => acc + period.weeks, 0);
    }

    get actual_instruction_weeks() {
        return this.periods.reduce((acc, period) => acc + period.actual_instruction_weeks, 0);
    }

    get courses_with_no_breaks() {
        return this.term_courses.filter(course => course.type === 'course');
    }

    get calendar_weeks() {
        return this.periods.reduce((acc, period) => acc + period.calculated_calendar_weeks, 0);
    }

    get has_overtime() {
        return this.periods.some(period => period.has_overtime);
    }
}
